import { extend, setInteractionMode } from 'vee-validate';
import {
  required,
  email,
  confirmed,
  image,
  ext,
  size,
  min_value as minValue,
  max_value as maxValue,
  min,
  max,
  length,
  numeric,
} from 'vee-validate/dist/rules';
import { formatDate, getISO8601Date } from '@/helpers/date';
import wordCount from '@/filters/word-count';

setInteractionMode('custom', ({ errors }) => {
  if (errors[0]) return { on: ['blur', 'input', 'change'] };
  return { on: [] };
});

extend('image', {
  ...image,
  message: '請上傳圖片',
});

extend('maxFiles', {
  validate(v, [maxFile]) {
    return v.length <= maxFile;
  },
  message: (_, params) => `最多上傳${params[0]}個檔案`,
});

extend('min', {
  ...min,
  message: '{_field_}最少為 {length} 個字',
});

extend('max', {
  ...max,
  message: '{_field_}最多為 {length} 個字',
});

extend('maxCode', {
  validate(codes, { maxCode }) {
    return codes.length <= maxCode;
  },
  message: (_, { maxCode, _field_ }) => `${_field_}最多為 ${maxCode} 張`,
  params: ['maxCode'],

});

extend('maxArray', {
  validate(codes, { maxLength }) {
    return codes.length <= maxLength;
  },
  // eg. custom: rules="maxArray:1,個"
  message: (_, { maxLength, unit, _field_ }) => `${_field_}最多為 ${maxLength} ${unit || '個'}`,
  params: ['maxLength', 'unit'],

});

extend('maxWord', {
  validate(v, { maxWord }) {
    return wordCount(v) <= maxWord;
  },
  message: (_, { maxWord, _field_ }) => `${_field_}最多為 ${maxWord} 個全形字`,
  params: ['maxWord'],
});

extend('minHKD', {
  ...minValue,
  message: (fieldName, placeholders) => `${fieldName}最少為HK$${placeholders.min}`,
});

extend('maxHKD', {
  ...maxValue,
  message: (fieldName, placeholders) => `${fieldName}最多為HK$${placeholders.max}`,
});

extend('minValue', {
  ...minValue,
  message: '{_field_}最小為{min}',
});

extend('maxValue', {
  ...maxValue,
  message: '{_field_}最大為{max}',
});

extend('minTag', {
  validate(v, { tagIds }) {
    const chosenTag = Object.values(v);
    const chosenTagArray = chosenTag.toString().split(',');
    let result = true;
    let tagIdArray = [];
    tagIds.forEach((tagId) => {
      if (tagId === null) { //  Should not happen
        tagIdArray = [];
      } else if (typeof (tagId) === 'number') {
        tagIdArray = [tagId.toString()];
      } else {
        tagIdArray = tagId.split(', ');
      }
      const tagIdArray2 = tagIdArray.filter((x) => !chosenTagArray.includes(x));
      if (tagIdArray2.length === 0) {
        result = false;
      }
    });
    if (result) {
      return true;
    }
    return false;
  },
  message: '每張優惠券必須有至少一個分類標籤',
  params: ['tagIds'],
});

extend('dateAfter', {
  validate(v, { minDate, needTime }) {
    return getISO8601Date(v, needTime) > getISO8601Date(minDate, needTime);
  },
  message: (_, { minDate, needTime, _field_ }) => {
    if (minDate) {
      return `請選擇${_field_} ${formatDate(minDate, needTime)} 之後`;
    }
    return '請選擇{_field_}';
  },
  params: ['minDate', 'needTime'],
});

extend('dateAfterMultiDates', {
  validate(v, { minDates, needTime }) {
    return minDates.every(
      (minDate) => getISO8601Date(v, needTime) > getISO8601Date(minDate, needTime),
    );
  },
  message: (_, { minDates, _field_ }) => {
    const maxMinDate = minDates.reduce((maxi, current) => {
      if (new Date(current) > new Date(maxi)) {
        return current;
      }
      return maxi;
    });

    if (minDates) {
      return `請選擇${_field_} ${maxMinDate} 之後`;
    }
    return '請選擇{_field_}';
  },
  params: ['minDates', 'needTime'],
});

extend('dateAfterOrEqualMultiDates', {
  validate(v, { minDates, needTime }) {
    return minDates.every(
      (minDate) => getISO8601Date(v, needTime) >= getISO8601Date(minDate, needTime),
    );
  },
  message: (_, { minDates, _field_ }) => {
    const maxMinDate = minDates.reduce((maxi, current) => {
      if (new Date(current) > new Date(maxi)) {
        return current;
      }
      return maxi;
    });

    if (minDates) {
      return `請選擇${_field_} ${maxMinDate} 之後`;
    }
    return '請選擇{_field_}';
  },
  params: ['minDates', 'needTime'],
});

extend('dateBeforeOrEqual', {
  validate(v, { target }) {
    return getISO8601Date(v, true) <= getISO8601Date(target, true);
  },
  message: (_, { target, _field_ }) => `${target}必須在${_field_}後才有效`,
  params: ['target'],
});

extend('dateBeforeOrEqualMultiDates', {
  validate(v, { target }) {
    return target.every(
      (date) => getISO8601Date(v, true) <= getISO8601Date(date, true),
    );
  },
  message: (_, { target, _field_ }) => `${target}必須在${_field_}後才有效`,
  params: ['target'],
});

extend('time', {
  validate(v) {
    // format HH:mm:ss
    return /^(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)$/.test(v);
  },
  message: '請輸入正確時間',
});

extend('ext', {
  ...ext,
  message: (field, params) => {
    const extensions = Object.keys(params)
      .filter((k) => parseInt(k, 10) >= 0)
      .map((k) => params[k])
      .join(', ');
    return `副檔名必須為${extensions}`;
  },
});

extend('size', {
  ...size,
  message: (field, params) => {
    const mb = params.size / 1024;
    return `檔案大小不能多於${Number.isInteger(mb) ? mb : mb.toFixed(2)}MB`;
  },
});

extend('brc', {
  validate(v) {
    // format 1234567891234567
    return /^\d{8}[-]?\d{3}[-]?(0[1-9]|1[012])[-]?\d{2}[-]?[A-Za-z0-9]$/.test(v);
  },
  message: '請輸入正確商業登記號碼',
});

extend('terms', {
  validate(v) {
    return v === true;
  },
  message: '請勾選項目',
});

extend('required', {
  ...required,
  message: '請輸入{_field_}',
});

extend('confirmed', {
  ...confirmed,
  message: '確認密碼必須與密碼相同',
});

extend('email', {
  ...email,
  message: '請輸入正確電郵格式',
});

extend('password', {
  validate(v) {
    return /^(?=.*[a-z])(?=.*[A-Z])[a-zA-Z\d]{8,}$/.test(v);
  },
  message: '密碼最少8個字，必須包含數字及大，小寫英文字母（不支援特殊符號）。',
});

// extend('domainName', {
//   validate(v) {
//     // http:// or https:// in front of domain name is not valid
//     return /^(?!:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/.test(v);
//   },
//   message: '請輸入正確{_field_}',
// });

extend('url', {
  validate(v) {
    return /^(http(s)?:\/\/)?[\u00ff-\uffff_a-zA-Z0-9.-]+(?:\.[\u00ff-\uffff_a-zA-Z0-9.-]+)+[\u00ff-\uffff_a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=.]+/.test(v);
  },
  message: '請輸入正確{_field_}',
});

extend('length', {
  ...length,
  message: '請輸入正確{_field_}',
});

extend('numeric', {
  ...numeric,
  message: '請輸入正確{_field_}',
});

extend('arrayLengthMax', {
  validate(v, { max: arrayLengthMax }) {
    return v.length <= arrayLengthMax;
  },
  message: (_, { max: arrayLengthMax, _field_ }) => `${_field_}最多為${arrayLengthMax}`,
  params: ['max'],
});

extend('username', {
  validate(v) {
    return /^[a-z0-9-_]+$/.test(v);
  },
  message: '請輸入正確帳戶名稱：使用小寫字母a-z、數字0-9或符號 _ 及 -',
});

extend('lowercase', {
  validate(v) {
    return /^[a-z][a-z0-9]*$/.test(v);
  },
  message: '請以細階輸入{_field_}',
});

extend('decimal', {
  validate(number, { numOfDecimalPlaces }) {
    const decimalPlaces = number.toString().split('.')[1];
    return decimalPlaces ? decimalPlaces.length <= numOfDecimalPlaces : true;
  },
  message: (_, { numOfDecimalPlaces, _field_ }) => `${_field_}最多為 ${numOfDecimalPlaces} 個小數點`,
  params: ['numOfDecimalPlaces'],
});

extend('ratio', {
  async validate(v) {
    const imageElement = new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(v[0]);
      } catch (e) {
        reject(e);
      }
    });

    const isValidRatio = await imageElement.then((value) => {
      const isValid = value.width / value.height === 1;
      return isValid;
    });

    return isValidRatio;
  },
  message: '只允許上傳1:1圖像',
});

// extend('enforceSubAccRules', {
//   validate(v) {
//     return (v !== 'qr' && v !== 'mc-qr');
//   },
//   message: '需先建立附屬帳戶以使用QR CODE功能',
// });
